import React, {
    createContext,
    useState,
    useEffect,
    useContext,
    useRef,
    useCallback,
} from 'react'
import { useClinic } from '~contexts/clinic-context'
import { auth } from '~config/firebase'
import { LocalStorageKeys } from '~config/constants'

import { useUserContext } from './user-context'

const INACTIVITY_TIMEOUT_DISABLED = -1 // A value of -1 disables the inactivity feature
const COUNTDOWN_START_MILLIS = 2 * 60 * 1000 // 2 minutes

/**
 * Inactivity state persisted to localStorage so the lock survives a page
 * reload or a tab being closed and reopened. Without this, a user could simply
 * refresh to escape the lock (Firebase keeps them signed in across reloads).
 *
 * `lastSignInTime` is Firebase's `User.metadata.lastSignInTime`; it changes on
 * every fresh sign-in, letting us tell a brand-new login (always starts
 * unlocked) apart from a reload of an existing session (re-evaluate idle time).
 */
interface PersistedInactivityState {
    lastSignInTime: string
    lastActivity: number
}

function readPersistedInactivityState(): PersistedInactivityState | null {
    if (typeof window === 'undefined') return null
    try {
        const raw = window.localStorage.getItem(LocalStorageKeys.inactivityState)
        if (!raw) return null
        const parsed = JSON.parse(raw) as Partial<PersistedInactivityState>
        if (
            typeof parsed.lastSignInTime !== 'string' ||
            typeof parsed.lastActivity !== 'number'
        ) {
            return null
        }
        return {
            lastSignInTime: parsed.lastSignInTime,
            lastActivity: parsed.lastActivity,
        }
    } catch {
        return null
    }
}

function writePersistedInactivityState(): void {
    if (typeof window === 'undefined') return
    try {
        const state: PersistedInactivityState = {
            lastSignInTime: auth.currentUser?.metadata?.lastSignInTime ?? '',
            lastActivity: Date.now(),
        }
        window.localStorage.setItem(
            LocalStorageKeys.inactivityState,
            JSON.stringify(state),
        )
    } catch {
        // Storage can fail (private mode, quota). The in-memory timer still
        // works; we just lose cross-reload enforcement in that edge case.
    }
}

interface InactivityContextType {
    isOpen: boolean
    isExpired: boolean
    countdown: number
    countdownStartMillis: number
    /**
     * Email of the user who was signed out when the session expired. Pre-filled
     * into the lock screen so they only need to re-enter their password.
     */
    lockedEmail: string
    resetInactivityTimer: () => void
}

const InactivityContext = createContext<InactivityContextType | null>(null)

export const useInactivityContext = () => {
    const context = useContext(InactivityContext)
    if (!context) {
        throw new Error(
            'useInactivityContext must be used within an InactivityProvider',
        )
    }
    return context
}

interface InactivityProviderProps {
    children: React.ReactNode
}

export const InactivityProvider: React.FC<InactivityProviderProps> = ({
    children,
}) => {
    const { clinic } = useClinic()
    const { user } = useUserContext()
    const [isOpen, setIsOpen] = useState(false)
    const [isExpired, setIsExpired] = useState(false)
    const [lockedEmail, setLockedEmail] = useState('')

    const inactivityTimerRef = useRef<number | null>(null)
    const countdownTimerRef = useRef<number | null>(null)
    const logoutTimerRef = useRef<number | null>(null)
    const lastActivityRef = useRef<number>(Date.now())
    const timeoutRef = useRef<number>(INACTIVITY_TIMEOUT_DISABLED)
    const countdownStartMillisRef = useRef<number>(COUNTDOWN_START_MILLIS)

    // Keep the current user's email in a ref so the (referentially stable)
    // expiry handler can pre-fill the lock screen without depending on `user`.
    const lockEmailRef = useRef<string>('')
    lockEmailRef.current = user?.email ?? auth.currentUser?.email ?? ''

    const timeout =
        clinic?.settings?.max_inactivity_duration || INACTIVITY_TIMEOUT_DISABLED
    const countdownStartMillis = Math.floor(
        Math.min(timeout / 2, COUNTDOWN_START_MILLIS),
    )

    // Update refs when values change to avoid stale closures
    timeoutRef.current = timeout
    countdownStartMillisRef.current = countdownStartMillis

    const [countdown, setCountdown] = useState(countdownStartMillis / 1000)

    const clearTimers = useCallback(() => {
        if (inactivityTimerRef.current !== null) {
            window.clearTimeout(inactivityTimerRef.current)
            inactivityTimerRef.current = null
        }
        if (countdownTimerRef.current !== null) {
            window.clearInterval(countdownTimerRef.current)
            countdownTimerRef.current = null
        }
        if (logoutTimerRef.current !== null) {
            window.clearTimeout(logoutTimerRef.current)
            logoutTimerRef.current = null
        }
        // Ensure any pending throttle timeout is cleared as well
        if (throttleRef.current !== null) {
            window.clearTimeout(throttleRef.current)
            throttleRef.current = null
        }
    }, [])

    const handleExpired = useCallback(() => {
        clearTimers()
        // Lock the screen in place. We deliberately keep the user signed in and
        // the page mounted so in-progress work (open forms, drafts, etc.) is
        // preserved behind an undismissable modal. The user must re-enter their
        // password to unlock — see `resetInactivityTimer` (the unlock path).
        setLockedEmail(lockEmailRef.current)
        setIsExpired(true)
        setIsOpen(true)
    }, [clearTimers])

    const resetInactivityTimer = useCallback(() => {
        clearTimers()
        setIsOpen(false)
        setIsExpired(false)
        setLockedEmail('')
        lastActivityRef.current = Date.now()
        // Persist the activity timestamp so idle time carries across reloads.
        writePersistedInactivityState()

        const currentTimeout = timeoutRef.current
        const currentCountdownStartMillis = countdownStartMillisRef.current

        if (currentTimeout === INACTIVITY_TIMEOUT_DISABLED || !user) {
            return
        }

        const timeUntilCountdown = Math.max(
            0,
            currentTimeout - currentCountdownStartMillis,
        )

        inactivityTimerRef.current = window.setTimeout(() => {
            setIsOpen(true)

            const countdownStartTime = Date.now()
            const countdownEndTime =
                countdownStartTime + currentCountdownStartMillis
            setCountdown(currentCountdownStartMillis / 1000)

            countdownTimerRef.current = window.setInterval(() => {
                const now = Date.now()
                const remainingMs = Math.max(0, countdownEndTime - now)
                const remainingSeconds = Math.ceil(remainingMs / 1000)

                setCountdown(remainingSeconds)

                if (remainingMs <= 0) {
                    handleExpired()
                }
            }, 1000)

            // Backup timeout to ensure expiry happens even if interval fails
            logoutTimerRef.current = window.setTimeout(
                handleExpired,
                currentCountdownStartMillis,
            )
        }, timeUntilCountdown)
    }, [user, handleExpired, clearTimers])

    // Throttled activity handler to prevent excessive resets
    const throttleRef = useRef<number | null>(null)

    // Keep the latest modal open state without re-creating callbacks
    const isOpenRef = useRef<boolean>(false)
    useEffect(() => {
        isOpenRef.current = isOpen
    }, [isOpen])

    const isExpiredRef = useRef<boolean>(false)
    useEffect(() => {
        isExpiredRef.current = isExpired
    }, [isExpired])

    const handleUserActivity = useCallback(() => {
        const now = Date.now()
        // Always update the last activity timestamp so timeout checks are accurate
        lastActivityRef.current = now

        // If session is expired, don't allow activity to reset - only reload button works
        if (isExpiredRef.current) {
            return
        }

        // If modal is open, clear any pending throttle and reset immediately to close it
        if (isOpenRef.current) {
            if (throttleRef.current !== null) {
                window.clearTimeout(throttleRef.current)
                throttleRef.current = null
            }
            resetInactivityTimer()
            return
        }

        // Throttle expensive timer resets to at most once per second
        // Only schedule if there isn't already a pending reset
        if (throttleRef.current === null) {
            throttleRef.current = window.setTimeout(() => {
                resetInactivityTimer()
                throttleRef.current = null
            }, 1000)
        }
    }, [resetInactivityTimer])

    const handleVisibilityChange = useCallback(() => {
        if (!document.hidden) {
            // If already expired, don't do anything
            if (isExpiredRef.current) {
                return
            }

            // When tab becomes visible, check if we should show expired state or reset timer
            const timeElapsed = Date.now() - lastActivityRef.current
            const currentTimeout = timeoutRef.current

            if (currentTimeout === INACTIVITY_TIMEOUT_DISABLED || !user) {
                return
            }

            // Clear any pending throttled activity handler
            if (throttleRef.current !== null) {
                window.clearTimeout(throttleRef.current)
                throttleRef.current = null
            }

            // If enough time has elapsed, show expired state
            if (timeElapsed >= currentTimeout) {
                handleExpired()
                return
            }

            // Always reset timer when returning to tab, treating it as user activity
            // This will close any frozen modal and restart fresh
            clearTimers()
            setIsOpen(false)
            setIsExpired(false)
            lastActivityRef.current = Date.now()

            const timeUntilCountdown = Math.max(
                0,
                currentTimeout - countdownStartMillisRef.current,
            )

            inactivityTimerRef.current = window.setTimeout(() => {
                setIsOpen(true)

                const countdownStartTime = Date.now()
                const countdownEndTime =
                    countdownStartTime + countdownStartMillisRef.current
                setCountdown(countdownStartMillisRef.current / 1000)

                countdownTimerRef.current = window.setInterval(() => {
                    const now = Date.now()
                    const remainingMs = Math.max(0, countdownEndTime - now)
                    const remainingSeconds = Math.ceil(remainingMs / 1000)

                    setCountdown(remainingSeconds)

                    if (remainingMs <= 0) {
                        handleExpired()
                    }
                }, 1000)

                logoutTimerRef.current = window.setTimeout(
                    handleExpired,
                    countdownStartMillisRef.current,
                )
            }, timeUntilCountdown)
        }
    }, [handleExpired, clearTimers, user])

    useEffect(() => {
        if (!user || timeout === INACTIVITY_TIMEOUT_DISABLED) {
            clearTimers()
            // If the user is authenticated but the feature is off (or the
            // clinic settings haven't reloaded yet), make sure we aren't stuck
            // on a stale lock screen — e.g. right after re-authenticating from
            // the inactivity lock. When there's no user we keep the lock state
            // so the lock screen stays up until they sign back in.
            if (user) {
                setIsOpen(false)
                setIsExpired(false)
                setLockedEmail('')
            }
            return
        }

        // Check if we're in the browser
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return
        }

        // Re-evaluate idle time across reloads. This closes the "just refresh to
        // get back in" hole: if this same session (matching Firebase sign-in)
        // has been idle past the timeout, start locked immediately. A fresh
        // sign-in has a new lastSignInTime and always starts unlocked.
        const persisted = readPersistedInactivityState()
        const currentSignInTime =
            auth.currentUser?.metadata?.lastSignInTime ?? ''
        const isSameSession =
            !!persisted && persisted.lastSignInTime === currentSignInTime
        const idleElapsed = persisted ? Date.now() - persisted.lastActivity : 0

        if (isSameSession && idleElapsed >= timeout) {
            handleExpired()
        } else {
            // Initialize the timer (also persists a fresh activity timestamp)
            resetInactivityTimer()
        }

        // Add event listeners
        window.addEventListener('mousemove', handleUserActivity, {
            passive: true,
        })
        window.addEventListener('keydown', handleUserActivity, {
            passive: true,
        })
        window.addEventListener('mousedown', handleUserActivity, {
            passive: true,
        })
        // Add input event listener to catch text input (paste, autocomplete, etc.)
        document.addEventListener('input', handleUserActivity, {
            passive: true,
            capture: true,
        })
        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            clearTimers()
            if (throttleRef.current !== null) {
                window.clearTimeout(throttleRef.current)
                throttleRef.current = null
            }
            window.removeEventListener('mousemove', handleUserActivity)
            window.removeEventListener('keydown', handleUserActivity)
            window.removeEventListener('mousedown', handleUserActivity)
            document.removeEventListener('input', handleUserActivity, {
                capture: true,
            })
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange,
            )
        }
    }, [
        timeout,
        user?.id,
        resetInactivityTimer,
        handleUserActivity,
        handleVisibilityChange,
        handleExpired,
        clearTimers,
    ])

    return (
        <InactivityContext.Provider
            value={{
                isOpen,
                isExpired,
                countdown,
                countdownStartMillis,
                lockedEmail,
                resetInactivityTimer,
            }}
        >
            {children}
        </InactivityContext.Provider>
    )
}
